import { motion } from "framer-motion";
import { OOL_LOGO_URL } from "../config/brand";

// Heavy-glow animated badge with the real ool logo at center.
// All animations stick to GPU-friendly properties (transform + opacity) to avoid
// the per-frame repaint cost of animating box-shadow.
export const SpinningLogo = ({ size = 340, className = "" }) => {
    const s = size;
    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className={`relative ${className}`}
            style={{ width: s, height: s, perspective: 900, willChange: "transform, opacity" }}
            data-testid="spinning-logo"
        >
            {/* Outer breathing cyan halo — transform/opacity only */}
            <motion.div
                aria-hidden
                className="absolute inset-0 rounded-full blur-3xl pointer-events-none"
                style={{
                    background:
                        "radial-gradient(circle at 50% 50%, rgba(34,211,238,0.65), rgba(125,211,252,0.3) 45%, transparent 70%)",
                    willChange: "transform, opacity",
                }}
                animate={{ opacity: [0.55, 0.9, 0.55], scale: [0.96, 1.06, 0.96] }}
                transition={{ duration: 4.2, repeat: Infinity, ease: "easeInOut" }}
            />

            {/* Outer dashed ring (slow reverse) */}
            <div className="absolute inset-0 spin-z-reverse" style={{ animationDuration: "32s", willChange: "transform" }}>
                <svg viewBox="0 0 200 200" className="w-full h-full" fill="none">
                    <circle cx="100" cy="100" r="96" stroke="url(#g-outer)" strokeWidth="0.8" strokeDasharray="2 5" />
                    <defs>
                        <linearGradient id="g-outer" x1="0" y1="0" x2="1" y2="1">
                            <stop offset="0%" stopColor="#22d3ee" stopOpacity="1" />
                            <stop offset="50%" stopColor="#7dd3fc" stopOpacity="0.85" />
                            <stop offset="100%" stopColor="#38bdf8" stopOpacity="0.6" />
                        </linearGradient>
                    </defs>
                </svg>
            </div>

            {/* Middle ring with cyan tick marks */}
            <div className="absolute inset-4 spin-z" style={{ animationDuration: "22s", willChange: "transform" }}>
                <svg viewBox="0 0 200 200" className="w-full h-full" fill="none">
                    <circle cx="100" cy="100" r="92" stroke="rgba(34, 211, 238, 0.45)" strokeWidth="0.6" />
                    {Array.from({ length: 48 }).map((_, i) => {
                        const angle = (i * 360) / 48;
                        const isMajor = i % 6 === 0;
                        return (
                            <line
                                key={i}
                                x1="100"
                                y1={isMajor ? "10" : "12"}
                                x2="100"
                                y2={isMajor ? "20" : "16"}
                                stroke={isMajor ? "#22d3ee" : "rgba(125, 211, 252, 0.4)"}
                                strokeWidth={isMajor ? "1.6" : "0.6"}
                                transform={`rotate(${angle} 100 100)`}
                            />
                        );
                    })}
                </svg>
            </div>

            {/* Inner 3D rotating disc — static box-shadow + sibling glow layer that breathes */}
            <div className="absolute inset-10 spin-y" style={{ animationDuration: "12s", willChange: "transform" }}>
                <div className="relative w-full h-full">
                    {/* Pulsing glow underlay (transform/opacity only, GPU) */}
                    <motion.div
                        aria-hidden
                        className="absolute -inset-2 rounded-full bg-cyan-400 blur-2xl pointer-events-none"
                        animate={{ opacity: [0.35, 0.75, 0.35], scale: [0.95, 1.06, 0.95] }}
                        transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
                        style={{ willChange: "transform, opacity" }}
                    />
                    <div
                        className="absolute inset-0 w-full h-full rounded-full overflow-hidden ring-2 ring-brand-cyan/70"
                        style={{
                            background:
                                "radial-gradient(circle at 30% 28%, rgba(34,211,238,0.25), rgba(0,0,0,0.95) 60%)",
                            boxShadow:
                                "0 0 38px rgba(34, 211, 238, 0.55), inset 0 0 42px rgba(34, 211, 238, 0.22)",
                        }}
                    >
                        <img
                            src={OOL_LOGO_URL}
                            alt="ools.me"
                            className="absolute inset-0 w-full h-full object-contain"
                            style={{ filter: "drop-shadow(0 0 18px rgba(34, 211, 238, 0.7))" }}
                        />
                        {/* Glassy highlight crescent */}
                        <div
                            className="absolute inset-0 rounded-full pointer-events-none"
                            style={{
                                background:
                                    "radial-gradient(circle at 22% 22%, rgba(255,255,255,0.22), transparent 38%)",
                            }}
                        />
                    </div>
                </div>
            </div>

            {/* Orbiting cyan dot — fast outer */}
            <div className="absolute inset-0 spin-z" style={{ animationDuration: "5.5s", willChange: "transform" }}>
                <div
                    className="absolute w-3 h-3 rounded-full bg-cyan-300"
                    style={{
                        top: -6,
                        left: "calc(50% - 6px)",
                        boxShadow: "0 0 22px 6px rgba(34, 211, 238, 0.95)",
                    }}
                />
            </div>

            {/* Counter-orbiting smaller dot */}
            <div className="absolute inset-3 spin-z-reverse" style={{ animationDuration: "8s", willChange: "transform" }}>
                <div
                    className="absolute w-2 h-2 rounded-full bg-sky-300"
                    style={{
                        top: -4,
                        left: "calc(50% - 4px)",
                        boxShadow: "0 0 16px 4px rgba(125, 211, 252, 0.85)",
                    }}
                />
            </div>

            {/* Third orbit */}
            <div className="absolute inset-6 spin-z" style={{ animationDuration: "12s", willChange: "transform" }}>
                <div
                    className="absolute w-1.5 h-1.5 rounded-full bg-white"
                    style={{
                        top: -3,
                        left: "calc(50% - 3px)",
                        boxShadow: "0 0 12px 3px rgba(255, 255, 255, 0.9)",
                    }}
                />
            </div>
        </motion.div>
    );
};
